import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Menu, X, Globe, Search } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
  const { t, language, changeLanguage, LANGUAGES } = useLanguage();
  const { totalItems } = useCart();
  const { items: wishlistItems } = useWishlist();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/shop?search=${encodeURIComponent(q)}`);
      setSearchOpen(false);
      setSearchQuery("");
      setMenuOpen(false);
    }
  };

  const currentLang = LANGUAGES.find((l) => l.code === language);

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-md border-b border-[#E6E2DD]/50"
    >
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Left Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-link" data-testid="nav-home">
              {t("nav.home")}
            </Link>
            <Link to="/shop" className="nav-link" data-testid="nav-shop">
              {t("nav.shop")}
            </Link>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>

          {/* Logo */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2"
            data-testid="logo-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_luxury-handbags-7/artifacts/unol57i8_IMG_3217.jpeg"
              alt="Mona Luxe"
              className="h-12 md:h-14 object-contain rounded-sm"
            />
          </Link>

          {/* Right Nav */}
          <div className="flex items-center gap-4 md:gap-6">
            {/* Search */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="opacity-70 hover:opacity-100 transition-opacity"
              data-testid="search-toggle-btn"
            >
              <Search size={18} strokeWidth={1.5} />
            </button>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 text-xs tracking-widest uppercase opacity-70 hover:opacity-100 transition-opacity"
                  data-testid="language-switcher"
                >
                  <Globe size={16} strokeWidth={1.5} />
                  <span className="hidden sm:inline">{currentLang?.code?.toUpperCase()}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-[#E6E2DD] min-w-[160px]">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`text-xs tracking-wide cursor-pointer ${
                      language === lang.code ? "font-bold" : ""
                    }`}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              className="relative opacity-70 hover:opacity-100 transition-opacity"
              data-testid="nav-wishlist"
            >
              <Heart size={20} strokeWidth={1.5} />
              {wishlistItems.length > 0 && (
                <span className="absolute -top-1 -right-1.5 w-4 h-4 bg-[#1A1A1A] text-[#FDFBF7] text-[9px] flex items-center justify-center">
                  {wishlistItems.length}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative opacity-70 hover:opacity-100 transition-opacity"
              data-testid="nav-cart"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1.5 w-4 h-4 bg-[#1A1A1A] text-[#FDFBF7] text-[9px] flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out bg-[#FDFBF7] border-b border-[#E6E2DD]/50 ${
          searchOpen ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="search-bar"
      >
        <div className="px-6 md:px-12 lg:px-24 py-3">
          <form onSubmit={handleSearch} className="flex items-center gap-3 max-w-xl mx-auto">
            <Search size={16} strokeWidth={1.5} className="text-[#9CA3AF] flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("search.placeholder")}
              className="flex-1 bg-transparent text-sm tracking-wide outline-none placeholder:text-[#9CA3AF] placeholder:tracking-wide"
              data-testid="search-input"
            />
            <button
              type="button"
              onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
              className="text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors"
              data-testid="search-close-btn"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#FDFBF7] border-t border-[#E6E2DD]/50 px-6 py-6" data-testid="mobile-menu">
          <form onSubmit={handleSearch} className="flex items-center gap-3 mb-4 pb-4 border-b border-[#E6E2DD]/50">
            <Search size={16} strokeWidth={1.5} className="text-[#9CA3AF] flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("search.placeholder")}
              className="flex-1 bg-transparent text-sm tracking-wide outline-none placeholder:text-[#9CA3AF]"
              data-testid="mobile-search-input"
            />
          </form>
          <nav className="flex flex-col gap-4">
            <Link
              to="/"
              className="nav-link text-sm"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.home")}
            </Link>
            <Link
              to="/shop"
              className="nav-link text-sm"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.shop")}
            </Link>
            <Link
              to="/wishlist"
              className="nav-link text-sm"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.wishlist")}
            </Link>
            <Link
              to="/cart"
              className="nav-link text-sm"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.cart")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
